import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Button, PageHeader, Progress, Table, Tag, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { broadcastsApi } from "../../api/broadcastsApi";
import ImportWizard from "../../components/business/broadcasts/ImportWizard";
import { IBroadcast, STATUS_COLORS } from "../../types/models/broadcasts";

const calendar = require("dayjs/plugin/calendar");
const day: any = dayjs.extend(calendar);

const Container = styled.div`
  /* Scrolls itself — see the note in BroadcastDetail.tsx. */
  height: 100%;
  overflow-y: auto;

  .muted {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }
`;

function Broadcasts() {
  const [rows, setRows] = useState<IBroadcast[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    broadcastsApi
      .list({ page, page_size: 25 })
      .then((data) => {
        setRows(data.results);
        setTotal(data.count);
      })
      .catch(() => message.error("Could not load broadcasts."))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(load, [load]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (text: string, row: IBroadcast) => (
        <div>
          <Link to={`/broadcasts/${row.id}`}>{text}</Link>
          {row.source_filename && (
            // The export filename is the single most useful clue about what a
            // list actually was, months later.
            <div className="muted">{row.source_filename}</div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (status: IBroadcast["status"]) => (
        <Tag color={STATUS_COLORS[status]}>{status}</Tag>
      ),
    },
    {
      title: "Template",
      dataIndex: "template_name",
      width: 220,
      ellipsis: true,
      render: (text: string, row: IBroadcast) =>
        text ? (
          <span>
            {text} <span className="muted">{row.template_language}</span>
          </span>
        ) : (
          <span className="muted">Not chosen yet</span>
        ),
    },
    {
      title: "Contacts",
      key: "contacts",
      width: 200,
      render: (_: any, row: IBroadcast) => {
        const { total: rowTotal, sendable, sent, delivered, read, failed } = row.counts;
        const done = sent + delivered + read + failed;
        // Before a send, the interesting number is how much of the file
        // survived import. During and after, it is how far the run got — two
        // different questions that would be a lie to show with one bar.
        if (done === 0) {
          return (
            <span>
              {sendable} of {rowTotal}
              {sendable < rowTotal && (
                <span className="muted"> ({rowTotal - sendable} excluded)</span>
              )}
            </span>
          );
        }
        return (
          <Progress
            percent={Math.round((done / Math.max(sendable, 1)) * 100)}
            size="small"
            status={failed > 0 ? "exception" : undefined}
            format={() => `${done}/${sendable}`}
          />
        );
      },
    },
    {
      title: "Created",
      dataIndex: "created_at",
      width: 170,
      render: (text: string, row: IBroadcast) => (
        <div>
          {day(text).calendar(null, { sameElse: "MMM D, YYYY" })}
          {row.created_by_name && <div className="muted">{row.created_by_name}</div>}
        </div>
      ),
    },
  ];

  return (
    <Container>
      <PageHeader
        title="WhatsApp broadcasts"
        subTitle="Send an approved template to a list of contacts"
        extra={[
          <Button
            key="new"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setImporting(true)}
          >
            New broadcast
          </Button>,
        ]}
      />

      <Table<IBroadcast>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        columns={columns as any}
        pagination={{
          current: page,
          pageSize: 25,
          total,
          showSizeChanger: false,
          onChange: setPage,
        }}
      />

      <ImportWizard
        open={importing}
        onClose={() => setImporting(false)}
        onCreated={(broadcast) => {
          setImporting(false);
          // Straight to the review screen. The import is the beginning of the
          // job, not the end of it — there are rows to look at.
          navigate(`/broadcasts/${broadcast.id}`);
        }}
      />
    </Container>
  );
}

export default Broadcasts;
